/**
 * LCP (Largest Contentful Paint) Optimizer
 * Optimizes loading of critical images for better performance
 */

class LCPOptimizer {
  constructor() {
    this.criticalImages = [
      '/images/carton.png', // CRITICAL: This is the LCP element (box-11)
      '/images/home_main.png', // CRITICAL: Hero image (box-1)
      '/images/bg.webp',
      '/images/home_unlimited_fun.png',
      '/images/campaign.webp'
    ];
    
    this.observer = null;
    this.loadedImages = new Set();
  }

  /**
   * Initialize LCP optimizations
   */
  init() {
    // Preload critical images
    this.preloadCriticalImages();
    
    // Setup intersection observer for lazy loading
    this.setupLazyLoading();
    
    // Optimize background image loading
    this.optimizeBackgroundImages();
    
    // Add responsive image loading
    this.setupResponsiveImages();
  }

  /**
   * Preload critical images that affect LCP
   */
  preloadCriticalImages() {
    this.criticalImages.forEach(originalSrc => {
      const src = this.getOptimizedImageUrl(originalSrc);
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = src;
      link.type = this.getImageType(src);
      
      // Add fetchpriority for critical images
      if (src.includes('carton.png') || src.includes('home_main.png')) {
        link.fetchpriority = 'high';
        // Also preload immediately with Image object for LCP elements
        const img = new Image();
        img.src = src;
      } else if (src.includes('bg.png') || src.includes('home_unlimited_fun.png')) {
        link.fetchpriority = 'high';
      }
      
      document.head.appendChild(link);
    });
  }

  /**
   * Setup lazy loading for non-critical images
   */
  setupLazyLoading() {
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              this.loadImage(entry.target);
            }
          });
        },
        {
          rootMargin: '50px 0px',
          threshold: 0.01
        }
      );
      
      // Observe all lazy-load images
      document.addEventListener('DOMContentLoaded', () => {
        const lazyImages = document.querySelectorAll('[data-lazy-src]');
        lazyImages.forEach(img => this.observer.observe(img));
      });
    }
  }

  /**
   * Load image when it comes into view
   */
  loadImage(element) {
    const src = element.dataset.lazySrc;
    if (!src || this.loadedImages.has(src)) return;
    
    if (element.tagName === 'IMG') {
      element.src = src;
    } else {
      // For background images
      element.style.backgroundImage = `url(${src})`;
    }
    
    this.loadedImages.add(src);
    element.removeAttribute('data-lazy-src');
    
    if (this.observer) {
      this.observer.unobserve(element);
    }
  }

  /**
   * Optimize background image loading
   */
  optimizeBackgroundImages() {
    // Load low-quality placeholder first
    const homeSection = document.querySelector('#home');
    if (homeSection) {
      // Create a low-quality placeholder
      const placeholder = 'data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAAKABADASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAf/xAAhEAACAQMDBQAAAAAAAAAAAAABAgMABAUGIWEREiMxUf/EABUBAQEAAAAAAAAAAAAAAAAAAAMF/8QAGhEAAgIDAAAAAAAAAAAAAAAAAAECEgMRkf/aAAwDAQACEQMRAD8AltJalv8AL5aKCHaztlC9vJ63LEWPBb1XQaQtVKjvJy5VQf/Z';
      
      homeSection.style.backgroundImage = `url(${placeholder})`;
      homeSection.style.filter = 'blur(5px)';
      homeSection.style.transition = 'filter 0.3s';
      
      // Load full quality image
      const img = new Image();
      img.src = '/images/bg.webp';
      img.onload = () => {
        homeSection.style.backgroundImage = `url(/images/bg.webp)`;
        homeSection.style.filter = 'none';
      };
    }
  }

  /**
   * Setup responsive image loading based on viewport
   */
  setupResponsiveImages() {
    const viewportWidth = window.innerWidth;
    const isMobile = viewportWidth <= 768;
    
    // Adjust parallax images based on device
    if (isMobile) {
      // Load smaller versions for mobile
      this.loadMobileImages();
    } else {
      // Load desktop versions
      this.loadDesktopImages();
    }
  }

  /**
   * Load mobile-optimized images
   */
  loadMobileImages() {
    const parallaxBoxes = document.querySelectorAll('.parallax');
    parallaxBoxes.forEach(box => {
      if (box.classList.contains('box-1') || box.classList.contains('box-11')) {
        // Prioritize critical LCP elements - load immediately
        box.style.backgroundImage = `url(${this.getImageSrc(box)})`;
        // Remove lazy loading attributes from critical elements
        box.removeAttribute('data-lazy-src');
      } else {
        // Lazy load other parallax images
        box.dataset.lazySrc = this.getImageSrc(box);
        box.style.backgroundImage = 'none';
      }
    });
  }

  /**
   * Load desktop images
   */
  loadDesktopImages() {
    const parallaxBoxes = document.querySelectorAll('.parallax');
    parallaxBoxes.forEach((box, index) => {
      if (box.classList.contains('box-1') || box.classList.contains('box-11')) {
        // Load critical LCP elements immediately
        box.style.backgroundImage = `url(${this.getImageSrc(box)})`;
        // Remove lazy loading attributes from critical elements
        box.removeAttribute('data-lazy-src');
      } else if (box.classList.contains('box-2')) {
        // Load box-2 immediately on desktop (above fold)
        box.style.backgroundImage = `url(${this.getImageSrc(box)})`;
      } else {
        // Lazy load others (box-3 and below)
        box.dataset.lazySrc = this.getImageSrc(box);
        box.style.backgroundImage = 'none';
      }
    });
  }

  /**
   * Get image source based on element class with WebP optimization
   */
  getImageSrc(element) {
    let src = '';
    if (element.classList.contains('box-1')) src = '/images/home_main.png';
    if (element.classList.contains('box-2')) src = '/images/home_unlimited_fun.png';
    if (element.classList.contains('box-3')) src = '/images/home_juices.png';
    if (element.classList.contains('box-11')) src = '/images/carton.png';
    
    // Return WebP version if supported
    return this.getOptimizedImageUrl(src);
  }

  /**
   * Get image MIME type
   */
  getImageType(src) {
    if (src.endsWith('.webp')) return 'image/webp';
    if (src.endsWith('.png')) return 'image/png';
    if (src.endsWith('.jpg') || src.endsWith('.jpeg')) return 'image/jpeg';
    return 'image/png';
  }

  /**
   * Convert image URLs to WebP if supported
   */
  getOptimizedImageUrl(originalUrl) {
    if (this.supportsWebP()) {
      // Replace extension with .webp
      return originalUrl.replace(/\.(png|jpg|jpeg)$/i, '.webp');
    }
    return originalUrl;
  }

  /**
   * Check WebP support
   */
  supportsWebP() {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    return canvas.toDataURL('image/webp').indexOf('image/webp') === 0;
  }
}

// Export for use in Vue app
export default new LCPOptimizer();